import { useEffect, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { X, Plus } from 'lucide-react';
import HelpTip from '../components/HelpTip';

import ClinicianManager from './ClinicianManager';   // no longer rendered here; kept for backwards-compat imports
import QuoteApprovalPanel from '../components/QuoteApprovalPanel';
import ProviderAliasPanel from '../components/ProviderAliasPanel';

const LISTS = [
  { key: 'clinics', label: 'Clinics' },
  { key: 'service_types', label: 'Service Types' },
  { key: 'referral_sources', label: 'Referral Sources' },
  { key: 'pre_auth_status', label: 'Pre-Auth Status' },
  { key: 'client_outcomes', label: 'Client Outcomes' },
  { key: 'outcome_reasons', label: 'Outcome Reasons' },
  { key: 'waitlist_status', label: 'Waitlist Status' },
  { key: 'insurance_types', label: 'Insurance Types' },
  { key: 'hold_reasons', label: 'Hold Reasons' },
];

export default function Setup() {
  const { user } = useAuth();
  const [setup, setSetup] = useState({});
  const [newVal, setNewVal] = useState({});

  // Iter 44: Super Admin now has full Setup access (billing_admin merged in).
  const isBillingOnly = false;
  const isFullAdmin = ['super_user', 'admin', 'hr_payroll'].includes(user?.role);
  const visibleLists = LISTS;

  useEffect(() => { api.get('/setup').then(r => setSetup(r.data || {})); }, []);

  const save = async (next) => {
    const payload = isBillingOnly ? { referral_sources: next.referral_sources || [] } : next;
    await api.put('/setup', payload);
    setSetup(next);
    toast.success('Saved');
  };

  const addItem = (key) => {
    const val = (newVal[key] || '').trim();
    if (!val) return;
    const next = { ...setup, [key]: [...(setup[key] || []), val] };
    save(next);
    setNewVal({ ...newVal, [key]: '' });
  };
  const remove = (key, idx) => {
    const next = { ...setup, [key]: setup[key].filter((_, i) => i !== idx) };
    save(next);
  };

  return (
    <div className="space-y-6" data-testid="setup-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-[#685B72]/70 font-semibold">Admin</div>
        <h1 className="font-serif text-4xl text-[#2D2833] mt-1">
          {isBillingOnly ? 'Referral Sources' : 'Setup & Dropdowns'}
        </h1>
        <p className="text-[#685B72] mt-1">
          {isBillingOnly
            ? 'Add or remove referral sources — these become available in the Inquiries form and BI report.'
            : 'Values you edit here become available across the app immediately.'}
        </p>
      </div>
      {isFullAdmin && (
        <p className="text-[12px] text-[#685B72] mb-2">Clinician roster now lives on its own tab in the sidebar — this page keeps the operational dropdowns only.</p>
      )}

      <div className="grid md:grid-cols-2 gap-6" data-testid="setup-lists">
        {visibleLists.map(({ key, label }) => (
          <Card key={key} className="rounded-2xl border-[#E8E5EC] p-6" data-testid={`setup-list-${key}`}>
            <h3 className="font-serif text-lg text-[#2D2833] mb-3 flex items-center gap-2">
              {label}
              {key === 'referral_sources' && (
                <HelpTip title="Referral Sources" testid="help-referral-sources">
                  Every place a lead can come from — pediatricians, ENTs, schools, families, marketing campaigns, etc. Adding one here immediately makes it available in the Inquiries form + the Client Pipeline referral dropdown + the Referrals BI report on Executive BI.
                </HelpTip>
              )}
              {key === 'clinics' && (
                <HelpTip title="Clinics" testid="help-clinics">
                  Your 9 physical locations. Adding a clinic here shows it in the sidebar and unlocks it as a target in every clinic-scoped dropdown.
                </HelpTip>
              )}
              {key === 'insurance_types' && (
                <HelpTip title="Insurance Types" testid="help-insurance">
                  The list of payers/insurance plans clients might have. Used by the pipeline + Executive BI to bucket revenue and denial rates by payer.
                </HelpTip>
              )}
            </h3>
            <div className="flex flex-wrap gap-2 mb-3">
              {(setup[key] || []).map((v, i) => (
                <span key={`${v}-${i}`} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F1EDF5] text-[#52475A] text-xs">
                  {v}
                  <button onClick={() => remove(key, i)} className="text-[#685B72]/70 hover:text-[#8A3A3A]" data-testid={`remove-${key}-${i}`}><X size={12} /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input placeholder="Add value…" className="rounded-xl" value={newVal[key] || ''} onChange={e => setNewVal({ ...newVal, [key]: e.target.value })} data-testid={`input-${key}`} />
              <Button onClick={() => addItem(key)} className="bg-[#685B72] hover:bg-[#52475A] text-white rounded-xl" data-testid={`add-${key}`}><Plus size={14} /></Button>
            </div>
          </Card>
        ))}
      </div>

      {/* Quote moderation queue — Admin + Super User */}
      {isFullAdmin && <QuoteApprovalPanel />}
      {user?.role === 'super_user' && <ProviderAliasPanel />}
    </div>
  );
}
